import React, { useState, useRef, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { DebtItem, PayoffMethod, MonthData } from './debtCalculatorTypes';
import { calculatePayoffPlan, formatCurrency } from './debtCalculatorUtils';
import DebtForm from './DebtForm';
import DebtResults from './DebtResults';

const DebtCalculator: React.FC = () => {
  const [debts, setDebts] = useState<DebtItem[]>([
    { id: '1', name: 'Credit Card', balance: 5000, interestRate: 18.99, minPayment: 120 },
    { id: '2', name: 'Student Loan', balance: 15000, interestRate: 5.5, minPayment: 180 }
  ]);
  const [monthlyPayment, setMonthlyPayment] = useState<number>(500);
  const [payoffMethod, setPayoffMethod] = useState<PayoffMethod>("avalanche");
  
  const [payoffPlan, setPayoffPlan] = useState<MonthData[]>([]);
  const [totalInterest, setTotalInterest] = useState<number>(0);
  const [payoffTime, setPayoffTime] = useState<number>(0);
  
  // Add ref for focus handling
  const calculatorRef = useRef<HTMLDivElement>(null);

  // Prevent scrolling to bottom when elements get focus
  useEffect(() => {
    // Function to prevent scroll on focus
    const preventScrollOnFocus = (e: Event) => {
      e.preventDefault();
      // Keep focus but prevent scrolling
      if (calculatorRef.current) {
        calculatorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    // Get all focusable elements within the calculator
    const focusableElements = calculatorRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    // Add event listeners to prevent scroll
    focusableElements?.forEach((el) => {
      el.addEventListener('focus', preventScrollOnFocus);
    });

    // Clean up
    return () => {
      focusableElements?.forEach((el) => {
        el.removeEventListener('focus', preventScrollOnFocus);
      });
    };
  }, [calculatorRef]); // Re-apply when ref changes

  const addDebt = () => {
    const newDebt: DebtItem = {
      id: Date.now().toString(),
      name: 'New Debt',
      balance: 0,
      interestRate: 0,
      minPayment: 0
    };
    
    setDebts([...debts, newDebt]);
  };

  const updateDebt = (id: string, field: keyof DebtItem, value: string | number) => {
    setDebts(debts.map(debt => 
      debt.id === id ? { ...debt, [field]: typeof value === 'string' ? value : Number(value) } : debt
    ));
  };

  const removeDebt = (id: string) => {
    if (debts.length <= 1) {
      toast({
        title: "Cannot remove all debts",
        description: "You must have at least one debt to use the calculator",
        variant: "destructive"
      });
      return;
    }
    
    setDebts(debts.filter(debt => debt.id !== id));
  };

  const calculatePayoff = () => {
    try {
      const totalMinPayment = debts.reduce((sum, debt) => sum + debt.minPayment, 0);
      
      if (monthlyPayment < totalMinPayment) {
        toast({
          title: "Payment too low",
          description: `Monthly payment must be at least ${formatCurrency(totalMinPayment)} to cover minimum payments`,
          variant: "destructive"
        });
        return;
      }

      // Use the utility function to calculate the payoff plan
      const result = calculatePayoffPlan(debts, monthlyPayment, payoffMethod);
      
      setPayoffPlan(result.plan);
      setTotalInterest(result.totalInterest);
      setPayoffTime(result.payoffTime);

      toast({
        title: "Calculation complete",
        description: "Your debt payoff plan has been calculated"
      });
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: "Calculation error",
          description: error.message,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Calculation error",
          description: "An error occurred during calculation",
          variant: "destructive"
        });
      }
    }
  };

  const totalDebt = debts.reduce((sum, debt) => sum + debt.balance, 0);

  return (
    <div className="space-y-6" ref={calculatorRef}>
      <Tabs defaultValue="input">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="input">Enter Debts</TabsTrigger>
          <TabsTrigger value="results" disabled={payoffPlan.length === 0}>Results</TabsTrigger>
        </TabsList>
        
        <TabsContent value="input" className="space-y-6 pt-4">
          <DebtForm 
            debts={debts}
            monthlyPayment={monthlyPayment}
            payoffMethod={payoffMethod}
            setMonthlyPayment={setMonthlyPayment}
            setPayoffMethod={setPayoffMethod}
            updateDebt={updateDebt}
            removeDebt={removeDebt}
            addDebt={addDebt}
            calculatePayoff={calculatePayoff}
          />
        </TabsContent>
        
        <TabsContent value="results" className="space-y-6 pt-4">
          <DebtResults 
            debts={debts}
            totalDebt={totalDebt}
            payoffPlan={payoffPlan}
            totalInterest={totalInterest}
            payoffTime={payoffTime}
            monthlyPayment={monthlyPayment}
            payoffMethod={payoffMethod}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DebtCalculator;
